import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';

// The Super Admin support queues (pages/support/SupportQueue.jsx).
//
// Ticket lists and the open conversation stay in the page's own state, fed by
// the support socket exactly as the tenant-admin inbox this page was ported
// from — a socket-driven list in redux would only add a second copy to keep
// in step. What DOES belong here is the assign/reassign picker's agent list:
// it comes over REST (`GET /platform-staff/agents?queue=`), is shared by every
// dropdown on the page, and is the one piece of support state another screen
// could want.

const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        return error.response.data?.message?.en
            || error.response.data?.message
            || error.message
            || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

/**
 * Who may take a ticket in this queue: super admins plus active platform staff
 * whose role holds support.respond + support.queue.<queue>. Each row carries
 * `{ id, name, email, image, roleName, isSuperAdmin, queues, openCount }` —
 * `id` is the business id (adminId / platformStaffId) the socket assign
 * events expect.
 */
export const fetchSupportAgents = createAsyncThunk(
    'supportQueue/fetchSupportAgents',
    async ({ queue }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/platform-staff/agents?queue=${encodeURIComponent(queue)}`);
            const data = response.data?.data ?? response.data;
            return { queue, agents: Array.isArray(data) ? data : [] };
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

const supportQueueSlice = createSlice({
    name: 'supportQueue',
    initialState: {
        agents: [],
        // Which queue the loaded list answers — the two queues can have
        // different pools, so a list fetched for one is not reused for the other.
        agentsQueue: null,
        agentsLoading: false,
        agentsError: null,
    },
    reducers: {
        clearSupportAgents: (state) => {
            state.agents = [];
            state.agentsQueue = null;
            state.agentsError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSupportAgents.pending, (state) => {
                state.agentsLoading = true;
                state.agentsError = null;
            })
            .addCase(fetchSupportAgents.fulfilled, (state, action) => {
                state.agentsLoading = false;
                state.agents = action.payload.agents;
                state.agentsQueue = action.payload.queue;
            })
            .addCase(fetchSupportAgents.rejected, (state, action) => {
                state.agentsLoading = false;
                state.agentsError = action.payload?.error || 'Failed to load support agents';
            });
    },
});

export const { clearSupportAgents } = supportQueueSlice.actions;
export default supportQueueSlice.reducer;
